import {
  ActivityIndicator,
  FlatList,
  type NativeScrollEvent,
  type NativeSyntheticEvent,
  Pressable,
  View,
} from "react-native"
import { useCallback, useEffect, useRef, useState } from "react"
import type { AnalysisType, UserType } from "@/generated/graphql"
import PropertyCard from "@/components/properties/PropertyCard"
import {
  type useAnalyses,
  useRemoveAnalysis,
  useSetAnalysisNotification,
  useSetFavoriteAnalysis,
} from "@/queries/analysis"
import { createBranchAnalysisShareLink } from "@/stores/branch"
import { useShare } from "@/hooks/useShare"
import { useUser } from "@/queries/user"
import {
  favoritesActionClicked,
  notificationActionClicked,
  propertiesListScrolled,
  propertyCardClicked,
} from "@/analytics/events/properties"
import { router, useGlobalSearchParams } from "expo-router"
import { useIsDesktop } from "@/hooks/useIsDesktop"
import LoadingBouncingBalls from "@/components/animations/LoadingBouncingBalls"
import { type EmptyAnalysisListType } from "@/types/analysisTypes"
import EmptyAnalysisList from "@/screens/analyze/components/EmptyAnalysisList"

const ITEM_HEIGHT = 300

export const AnalysisList = ({
  analysesQuery,
  onlyFavorites = false,
}: {
  analysesQuery: ReturnType<typeof useAnalyses>
  onlyFavorites?: boolean
}) => {
  const scrollTo = useRef(0)
  const lastRecordedScrollTo = useRef(0)
  const {
    data: analyses,
    isLoading,
    refetch,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  } = analysesQuery
  const removeAnalysisMutation = useRemoveAnalysis()
  const analysisNotificationMutation = useSetAnalysisNotification()
  const favoriteAnalysisMutation = useSetFavoriteAnalysis()

  const [refreshing, setRefreshing] = useState(false)
  const [analysisList, setAnalysisList] = useState<any[] | null>(null)
  const share = useShare()
  const { data: user } = useUser()

  const isDesktop = useIsDesktop()
  const globalSearchParams = useGlobalSearchParams()

  useEffect(() => {
    if (analyses) {
      const newList: AnalysisType[] = []
      const existingIds = new Set()
      analyses.pages.forEach((page) => {
        page?.analyses?.forEach((analysis) => {
          if (!existingIds.has(analysis.id)) {
            newList.push(analysis as AnalysisType)
            existingIds.add(analysis.id)
          }
        })
      })
      setAnalysisList(newList)
    }
  }, [analyses])

  const incrementPage = () => {
    if (hasNextPage && !isFetchingNextPage) {
      void fetchNextPage()
    }
  }

  function toggleNotifications(item: AnalysisType) {
    const notify: boolean = !item.notificationsEnabled
    item.notificationsEnabled = notify

    const newList = [...(analysisList || [])]
    notificationActionClicked(item.id, "Properties", notify)
    setAnalysisList(newList)

    analysisNotificationMutation.mutate(
      { analysisId: item.id, enableNotifications: notify },
      {
        onError: (err) => {
          console.error("Toggle Card Notification Error:", err)
        },
      }
    )
  }

  function favoriteAnalysis(item: AnalysisType) {
    const favorite = !item.isFavorite

    if (onlyFavorites) {
      const newList = [
        ...(analysisList || []).filter((analysis) => analysis.id !== item.id),
      ]
      setAnalysisList(newList)
    } else {
      item.isFavorite = favorite
      const newList = [...(analysisList || [])]
      favoritesActionClicked("Properties", favorite)
      setAnalysisList(newList)
    }
    favoriteAnalysisMutation.mutate(
      { analysisId: item.id, favorite },
      {
        onError: (err) => {
          console.error(err)
        },
      }
    )
  }

  const renderProperty = useCallback(
    ({ item, index }: { item: AnalysisType; index: number }) => (
      <Pressable
        testID={`analysis-${index}`}
        className="h-[300px]"
        onPress={() => {
          onAnalysisPress(item, index)
        }}
      >
        <PropertyCard
          selected={isDesktop && globalSearchParams?.analysisId === item.id}
          analysis={item}
          onShare={shareAnalysis}
          onRemove={removeAnalysis}
          onNotify={() => {
            toggleNotifications(item)
          }}
          onFavorite={() => {
            favoriteAnalysis(item)
          }}
        />
      </Pressable>
    ),
    [analysisList, globalSearchParams]
  )

  const onAnalysisPress = (analysis: AnalysisType, index: number) => {
    propertyCardClicked({
      cardListNumber: index,
      propertyType: analysis.isSourced
        ? "sourced"
        : analysis.sharedFrom
          ? "shared"
          : "analysis",
      propertyAddress: analysis.property.fullAddress,
      roi: analysis.roiDealScore || "",
      price: analysis.purchasePrice || 0,
      arv: analysis.afterRepairValue || 0,
      profit: analysis.profitPotential || 0,
      isNew: !analysis.readAt,
    })
    router.push(`/properties/${analysis.id}`)
  }

  const onPullRefresh = () => {
    void (async () => {
      setRefreshing(true)
      await refetch()
      setRefreshing(false)
    })()
  }

  const removeAnalysis = (id: string) => {
    const newList = [
      ...(analysisList || []).filter((analysis) => analysis.id !== id),
    ]
    setAnalysisList(newList)
    removeAnalysisMutation.mutate(
      { analysisId: id },
      {
        onError: (err) => {
          console.error(err)
        },
      }
    )
  }

  const onListScroll = (event: NativeSyntheticEvent<NativeScrollEvent>) => {
    scrollTo.current = event.nativeEvent.contentOffset.y
  }

  const recordScrollIndex = () => {
    if (lastRecordedScrollTo.current !== scrollTo.current) {
      lastRecordedScrollTo.current = scrollTo.current
      propertiesListScrolled({
        scrolledTo: Math.ceil(scrollTo.current / ITEM_HEIGHT),
      })
    }
  }

  const shareAnalysis = (
    id: string,
    street: string,
    imageUrl: string | null
  ) => {
    void (async () => {
      const branchUrl = await createBranchAnalysisShareLink({
        user: user?.me as UserType,
        analysisId: id,
        street,
        imageUrl,
      })
      console.log(branchUrl)

      await share({
        message: `Check out the RE investment analysis I've created using the Backflip app for ${street}.`,
        url: branchUrl,
        title: `Backflip Analysis for ${street}`,
        subject: `Backflip Analysis for ${street}`,
      })
    })()
  }

  const ListEndLoader = () => {
    if (isLoading) {
      return <ActivityIndicator size={"large"} />
    }
  }

  if (analysisList === null) {
    return (
      <View className="flex items-center justify-center flex-1">
        <LoadingBouncingBalls />
      </View>
    )
  }

  const listIsEmpty = analysisList?.length === 0
  const currentListType: EmptyAnalysisListType = onlyFavorites
    ? "favorites"
    : "all"

  // Empty List Handling
  if (listIsEmpty) {
    return <EmptyAnalysisList type={currentListType} />
  }

  return (
    <View className="flex flex-1">
      <FlatList
        data={analysisList}
        onEndReached={incrementPage}
        onEndReachedThreshold={0.8}
        renderItem={renderProperty}
        onScroll={onListScroll}
        onMomentumScrollEnd={recordScrollIndex}
        scrollEventThrottle={16}
        getItemLayout={(_, index) => ({
          length: ITEM_HEIGHT,
          offset: ITEM_HEIGHT * index,
          index,
        })}
        ListFooterComponent={ListEndLoader}
        keyExtractor={(analysis) => analysis.id}
        onRefresh={onPullRefresh}
        refreshing={refreshing}
      />
    </View>
  )
}
